import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { CbhIcon, Rating, Text } from "@src/appV2/redesign/system/ui-components";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { RATING_AGGREGATE_EMPTY } from "@src/appV2/redesign/Workplace/Review/constants";

import { type FacilityTypeMetadata } from "./api/useFetchPaginatedShiftBlocks";

interface ShiftBlockFacilityDetailsProps {
  facility: FacilityTypeMetadata["attributes"] & { id: string };
}

export function ShiftBlockFacilityDetails(props: ShiftBlockFacilityDetailsProps) {
  const { facility } = props;

  const { data: facilityRatings = RATING_AGGREGATE_EMPTY, isSuccess: isSuccessRatings } =
    useGetRatingAggregates(facility.id, {
      enabled: isDefined(facility.id),
    });

  const isExtraTimePayEnabled =
    isDefined(facility.extraTimePaySettings?.workdayOptions) ||
    isDefined(facility.extraTimePaySettings?.weeklyOption);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Text semibold>{facility.name}</Text>
        <Text
          variant="body2"
          sx={{ color: (theme) => theme.palette.text.secondary }}
          aria-label="Facility Distance"
        >
          {formatDistanceInMiles(facility.distance)}. from home
        </Text>
      </Stack>
      {isSuccessRatings && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Rating readOnly value={facilityRatings.avgRating} size="small" />
          <Text variant="body2">
            {facilityRatings.avgRating.toFixed(1)} ({facilityRatings.numberOfRatings} reviews)
          </Text>
        </Stack>
      )}
      {isExtraTimePayEnabled && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ color: (theme) => theme.palette.primary.main }}
        >
          <CbhIcon type="stop-watch" size="small" color={(theme) => theme.palette.primary.main} />
          <Text color="inherit" variant="body2">
            Extra Time Pay Facility
          </Text>
        </Stack>
      )}
    </Stack>
  );
}
